// The RBP Shopping List's benefit group rows (RBP-D14, RBP-D16, RBP-D38), as SL1.0's report lists
// them (`rbp_report.htm:13-100`): Category (with the non-payment mark), Qualification (a coloured
// dot and the percentage purchased), Cluster, Min, Max, Products, In cart and Selected (the count
// alone: SL1.0's check mark read as a checkbox that could not be clicked, so it is gone, ADO-4037).
// A table on desktop, on a white card with the Card component's corners, its expanded rows white
// too; below it, one card per group in SL1.0's mobile grid. Rows keep the server's order
// (benefit-group code order).
//
// The colour is the server's traffic light (green at or above highly eligible, orange at or above
// eligible, else red), drawn as a status badge whose text carries the value, never colour alone.
// Each row takes `renderRowExtra`: the slot the page fills with the group's menu.
//
// The Category cell carries the expand control: one group open at a time, as SL1.0's toggle
// (`rbp_npr.js:5-72`). On desktop the open group is drawn in a full-width row under its own; on a
// tablet or phone, inside its card. The row header itself is not a button, so the group's menu
// keeps its place. A bump of `collapseSignal` closes the open group (SL1.0 collapses it after a
// selection is cleared, `rbp_npr.js:137-150`).
import { useEffect, useState, type ReactNode } from 'react';
import {
  Collapse,
  Icon,
  StatusBadge,
  Table,
  useHeaderLayout,
  type StatusTone,
} from '../../design-system';
import type { RbpBenefitGroupRow, RbpTrafficLight } from '../../api/rbp.client';
import type { RbpPendingSelections } from '../../rbp/rbpPendingSelections';
import { BenefitGroupLines } from './BenefitGroupLines';
import styles from './BenefitGroupTable.module.css';

const TONE: Readonly<Record<RbpTrafficLight, StatusTone>> = {
  GREEN: 'success',
  ORANGE: 'warning',
  RED: 'error',
};

const TRAFFIC_LIGHT_LABEL: Readonly<Record<RbpTrafficLight, string>> = {
  GREEN: 'highly eligible',
  ORANGE: 'eligible',
  RED: 'not yet eligible',
};

/** A line count from the report (min, max), as its number: never rounded (RBP-D15). */
function figure(value: number): string {
  return String(value);
}

/**
 * The percentage purchased at the two places its `numeric(5,2)` column stores, as the report holds
 * it and SL1.0 printed it ("50.00"). The value never has more than two places, so this only
 * restores the trailing zeros a JSON number drops; it never rounds (RBP-D15).
 */
function percentage(value: number): string {
  return value.toFixed(2);
}

/** The id of a group's expanded region, for its toggle's `aria-controls`. */
function linesRegionId(row: RbpBenefitGroupRow): string {
  return `rbp-group-lines-${row.benefitGroupCode.replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

function ExpandToggle({
  row,
  open,
  onToggle,
}: {
  readonly row: RbpBenefitGroupRow;
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.expand}
      aria-expanded={open}
      aria-controls={open ? linesRegionId(row) : undefined}
      // A constant name: `aria-expanded` alone carries open or closed.
      aria-label={`Products in ${row.description}`}
      onClick={onToggle}
    >
      <Icon name={open ? 'caretUp' : 'caretDown'} size={16} />
    </button>
  );
}

function Category({ row }: { readonly row: RbpBenefitGroupRow }) {
  return (
    <span className={styles.category}>
      {row.description}
      {row.isRebatePayable ? null : (
        <span className={styles.nonPayable} title="Non-payment RBP category">
          <Icon name="xCircle" size={14} />
          <span className={styles.visuallyHidden}>Non-payment RBP category</span>
        </span>
      )}
    </span>
  );
}

function Qualification({ row }: { readonly row: RbpBenefitGroupRow }) {
  return (
    <span data-traffic-light={row.trafficLight}>
      <StatusBadge tone={TONE[row.trafficLight]}>
        {`${percentage(row.purchasedPct)} %`}
        <span
          className={styles.visuallyHidden}
        >{`, ${TRAFFIC_LIGHT_LABEL[row.trafficLight]}`}</span>
      </StatusBadge>
    </span>
  );
}

function Selected({ row }: { readonly row: RbpBenefitGroupRow }) {
  return <span className={styles.selected}>{row.productsSelected}</span>;
}

export function BenefitGroupTable({
  rows,
  renderRowExtra,
  onSelectionSaved,
  collapseSignal = 0,
  pending,
}: {
  readonly rows: readonly RbpBenefitGroupRow[];
  readonly renderRowExtra?: (row: RbpBenefitGroupRow) => ReactNode;
  /** Passed to the open group's lines: called after each stored quantity. */
  readonly onSelectionSaved?: () => void;
  /** Each new value closes the open group. */
  readonly collapseSignal?: number;
  /** Tracks the open group's in-flight selection writes so a clear can let them settle first. */
  readonly pending?: RbpPendingSelections;
}) {
  const layout = useHeaderLayout();
  const [openCode, setOpenCode] = useState<string | null>(null);
  useEffect(() => {
    setOpenCode(null);
  }, [collapseSignal]);
  const isOpen = (row: RbpBenefitGroupRow) => openCode === row.benefitGroupCode;
  const toggle = (row: RbpBenefitGroupRow) =>
    setOpenCode((current) => (current === row.benefitGroupCode ? null : row.benefitGroupCode));

  if (layout === 'desktop') {
    return (
      <div className={styles.tableCard}>
        <Table<RbpBenefitGroupRow>
          caption="Benefit groups"
          rowKey={(row) => row.benefitGroupCode}
          rows={[...rows]}
          emptyMessage="No benefit groups in the current report."
          columns={[
            {
              key: 'category',
              header: 'Category',
              render: (row) => (
                <span className={styles.categoryCell}>
                  <ExpandToggle row={row} open={isOpen(row)} onToggle={() => toggle(row)} />
                  <Category row={row} />
                </span>
              ),
            },
            {
              key: 'qualification',
              header: 'Qualification',
              render: (row) => <Qualification row={row} />,
            },
            { key: 'cluster', header: 'Cluster', render: (row) => row.clusterCode ?? '' },
            { key: 'min', header: 'Min', render: (row) => figure(row.minLinesRequired) },
            { key: 'max', header: 'Max', render: (row) => figure(row.maxLinesRequired) },
            { key: 'products', header: 'Products', render: (row) => row.productCount },
            { key: 'inCart', header: 'In Cart', render: (row) => row.productsInCarts },
            {
              key: 'selected',
              header: 'Selected',
              // One centred row, so the count lines up with the other figures beside the menu.
              render: (row) => (
                <span className={styles.selectedCell}>
                  <Selected row={row} />
                  {renderRowExtra?.(row)}
                </span>
              ),
            },
          ]}
          renderExpandedRow={(row) =>
            isOpen(row) ? (
              <BenefitGroupLines
                row={row}
                id={linesRegionId(row)}
                onSelectionSaved={onSelectionSaved}
                pending={pending}
              />
            ) : null
          }
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return <p className={styles.empty}>No benefit groups in the current report.</p>;
  }
  return (
    <ul className={styles.cards} aria-label="Benefit groups">
      {rows.map((row) => (
        <li key={row.benefitGroupCode} className={styles.card} aria-label={row.description}>
          <div className={styles.cardHeader}>
            <span className={styles.label}>Category</span>
            <span className={`${styles.cardTitle} ${styles.categoryCell}`}>
              <ExpandToggle row={row} open={isOpen(row)} onToggle={() => toggle(row)} />
              <Category row={row} />
            </span>
          </div>
          <dl className={styles.grid}>
            <div>
              <dt className={styles.label}>Qualification</dt>
              <dd>
                <Qualification row={row} />
              </dd>
            </div>
            <div>
              <dt className={styles.label}>Max</dt>
              <dd>{figure(row.maxLinesRequired)}</dd>
            </div>
            <div>
              <dt className={styles.label}>Products</dt>
              <dd>{row.productCount}</dd>
            </div>
            <div>
              <dt className={styles.label}>Cluster</dt>
              <dd>{row.clusterCode ?? ''}</dd>
            </div>
            <div>
              <dt className={styles.label}>Min</dt>
              <dd>{figure(row.minLinesRequired)}</dd>
            </div>
            <div>
              <dt className={styles.label}>In cart</dt>
              <dd>{row.productsInCarts}</dd>
            </div>
          </dl>
          <div className={styles.cardFoot}>
            <span className={styles.label}>Selected</span>
            <Selected row={row} />
          </div>
          {renderRowExtra?.(row)}
          <Collapse open={isOpen(row)}>
            {isOpen(row) ? (
              <BenefitGroupLines
                row={row}
                id={linesRegionId(row)}
                onSelectionSaved={onSelectionSaved}
                pending={pending}
              />
            ) : null}
          </Collapse>
        </li>
      ))}
    </ul>
  );
}
